import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import apiClient from "services/ApiClient";
import { User } from "features/User/models";

export interface CalendarPermission {
  id: string;
  role: string;
  user: User;
}

export interface CalendarServiceCalendar {
  id: string;
  name: string;
  default: boolean;
  subscriptionUrl: string;
  calendarPermissions?: CalendarPermission[];
}

export const calendarKeys = {
  all: ["calendars"] as const,
  lists: () => [...calendarKeys.all, "list"] as const,
  details: () => [...calendarKeys.all, "detail"] as const,
  detail: (id: string) => [...calendarKeys.details(), id] as const,
  permissions: (calendarId: string) => [...calendarKeys.detail(calendarId), "permissions"] as const,
  permission: (calendarId: string, permissionId: string) => [...calendarKeys.permissions(calendarId), permissionId] as const,
};

export function setCalendarInQueryClient(queryClient: QueryClient, calendar: CalendarServiceCalendar) {
  queryClient.setQueryData(calendarKeys.detail(calendar.id), (oldData: CalendarServiceCalendar | undefined) => {
    if (!oldData) {
      return calendar;
    }
    return {
      ...oldData,
      ...calendar,
    };
  });
}

export const useCalendarsQuery = (options = {}) => useQuery<CalendarServiceCalendar[]>({
  queryKey: calendarKeys.lists(),
  queryFn: async () => apiClient.get("/api/calendars"),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
});

export function useCreateCalendar() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { calendarUrl: string; default?: boolean }) => {
      return await apiClient.post<CalendarServiceCalendar>("/api/calendars", data);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Calendar added successfully",
        status: "success",
        duration: 3000,
      });
      queryClient.invalidateQueries({ queryKey: calendarKeys.lists() });
    },
    onError: (error: any) => {
      console.error("Failed to create calendar:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to add calendar",
        status: "error",
        duration: 5000,
      });
    },
  });
}

export const useCalendarDetailQuery = (id: string | undefined, options = {}) => useQuery<CalendarServiceCalendar>({
  queryKey: calendarKeys.detail(id as string),
  queryFn: async () => apiClient.get(`/api/calendars/${id}`),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useCalendarPermissionsQuery = (calendarId: string, options = {}) => useQuery<CalendarPermission[]>({
  queryKey: calendarKeys.permissions(calendarId),
  queryFn: async () => apiClient.get(`/api/calendars/${calendarId}/permissions`),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!calendarId && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

function useCalendarPermissionRequest(
  mutationFn: (data: any) => Promise<any>,
  resetQueryClient: boolean = false,
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res, variables) => {
      if (resetQueryClient) {
        queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      } else {
        queryClient.invalidateQueries({ queryKey: calendarKeys.lists() });
      }
      toast({
        title: "Success",
        description: "Calendar permission updated successfully",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      console.log(error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "There was an error, please try again.",
        status: "error",
        duration: 5000,
      });
    },
  });
}

export function useAddCalendarPermission() {
  return useCalendarPermissionRequest(async (data: { calendarId: string; userId: string; role: string }) => {
    const response = await apiClient.post(`/api/calendars/${data.calendarId}/permissions`, {
      userId: data.userId,
      role: data.role,
    });
    return response.data;
  });
}

export function useUpdateCalendarPermission() {
  return useCalendarPermissionRequest(async (data: { calendarId: string; permissionId: string; role: string }) => {
    const response = await apiClient.put(`/api/calendars/${data.calendarId}/permissions/${data.permissionId}`, {
      role: data.role,
    });
    return response.data;
  });
}

export function useRemoveCalendarPermission() {
  return useCalendarPermissionRequest(async (data: { calendarId: string; permissionId: string }) => {
    await apiClient.delete(`/api/calendars/${data.calendarId}/permissions/${data.permissionId}`);
  });
}
